import React, { useEffect, useMemo, useRef, useState } from "react";
import GoodBoiStage from "./GoodBoiCharacter";
import {
  extractAssistantMeta,
  extractDisplayText,
  generateId,
  getOrCreate,
  spokenText,
} from "./brainParse";
import "./App.css";

const API_URL = "";

const MaskMark = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <path d="M32 6c10 0 18 8 18 18v16c0 10-8 18-18 18S14 50 14 40V24C14 14 22 6 32 6Z" fill="rgba(255,255,255,0.8)" />
    <path d="M18 26c5-6 23-6 28 0" fill="none" stroke="#7c3aed" strokeWidth="6" strokeLinecap="round" />
    <ellipse cx="24" cy="32" rx="6" ry="4" fill="white" />
    <ellipse cx="40" cy="32" rx="6" ry="4" fill="white" />
  </svg>
);

export default function App() {
  const sessionId = useMemo(() => getOrCreate("goodboi_session"), []);
  const userId = useMemo(() => getOrCreate("goodboi_user"), []);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [debugLastAiRaw, setDebugLastAiRaw] = useState(null);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("goodboi_muted") === "1");
  const [speaking, setSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [pose, setPose] = useState({ emotion: "neutral", animation: "idle", voice_style: "neutral" });
  const [caption, setCaption] = useState("");

  const videoRef = useRef(null);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(0);
  const freqRef = useRef(new Uint8Array(128));

  const latestMeta = pose;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, loading]);

  const stopAnalyser = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    setAudioLevel(0);
  };

  const pumpAnalyser = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    analyser.getByteFrequencyData(freqRef.current);
    let sum = 0;
    for (let i = 0; i < 24; i++) sum += freqRef.current[i];
    setAudioLevel(Math.min(1, sum / (24 * 180)));
    rafRef.current = requestAnimationFrame(pumpAnalyser);
  };

  const attachAnalyser = (audio) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    if (!sourceRef.current) {
      sourceRef.current = ctx.createMediaElementSource(audio);
      analyserRef.current = ctx.createAnalyser();
      analyserRef.current.fftSize = 256;
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(ctx.destination);
    }
    stopAnalyser();
    pumpAnalyser();
  };

  const stopVoice = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    stopAnalyser();
    setSpeaking(false);
  };

  const speakBrowser = (text, voiceStyle) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.pitch = voiceStyle === "bright" ? 1.25 : voiceStyle === "soft" ? 0.95 : 1.08;
    utter.rate = voiceStyle === "bright" ? 1.08 : voiceStyle === "soft" ? 0.92 : 1;
    utter.onstart = () => {
      setSpeaking(true);
      const start = performance.now();
      const tick = () => {
        if (!window.speechSynthesis.speaking) {
          setAudioLevel(0);
          return;
        }
        setAudioLevel(0.25 + Math.abs(Math.sin((performance.now() - start) / 90)) * 0.55);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    };
    utter.onend = () => {
      stopAnalyser();
      setSpeaking(false);
      setPose((p) => ({ ...p, animation: p.animation === "talk" ? "idle" : p.animation }));
    };
    window.speechSynthesis.speak(utter);
  };

  const speak = async (text, voiceStyle = "neutral") => {
    const line = spokenText(text);
    if (!line || muted) return;
    stopVoice();
    setCaption(line);
    try {
      const res = await fetch(`${API_URL}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: line, voice_style: voiceStyle }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      if (!blob.type.includes("audio") && blob.size < 200) throw new Error("not audio");
      const url = URL.createObjectURL(blob);
      const audio = audioRef.current || new Audio();
      audioRef.current = audio;
      audio.src = url;
      attachAnalyser(audio);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        stopAnalyser();
        setSpeaking(false);
        setPose((p) => ({ ...p, animation: "idle" }));
      };
      await audio.play();
      setSpeaking(true);
    } catch (err) {
      console.warn("TTS fallback:", err);
      speakBrowser(line, voiceStyle);
    }
  };

  const loadMemory = async () => {
    try {
      const res = await fetch(
        `${API_URL}/memory?session_id=${encodeURIComponent(sessionId)}&user_id=${encodeURIComponent(userId)}`
      );
      const data = await res.json();
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.memories)
        ? data.memories
        : Array.isArray(data?.history)
        ? data.history
        : Array.isArray(data?.messages)
        ? data.messages
        : [];
      const formatted = [...arr]
        .sort((a, b) => (Date.parse(a?.timestamp || "") || 0) - (Date.parse(b?.timestamp || "") || 0))
        .map((entry) => {
          const roleRaw = String(entry?.role || "").toLowerCase();
          const role = roleRaw === "assistant" || roleRaw === "ai" ? "ai" : "user";
          const rawContent = entry?.content ?? "";
          return {
            id: generateId(),
            role,
            timestamp: entry?.timestamp,
            rawContent,
            meta: role === "ai" ? extractAssistantMeta(rawContent) : null,
          };
        });
      setMessages(formatted);
      const lastAi = [...formatted].reverse().find((m) => m.role === "ai");
      if (lastAi?.meta) {
        setPose({
          emotion: lastAi.meta.emotion || "neutral",
          animation: lastAi.meta.animation || "idle",
          voice_style: lastAi.meta.voice_style || "neutral",
        });
        setCaption(extractDisplayText("assistant", lastAi.rawContent));
      }
    } catch (err) {
      console.error("Memory loading failed:", err);
    }
  };

  useEffect(() => {
    loadMemory();
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraEnabled(true);
    } catch {
      alert("Camera denied");
      setCameraEnabled(false);
    }
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream && typeof stream.getTracks === "function") stream.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraEnabled(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      setImage(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.readAsDataURL(file);
  };

  const sendPayload = async (payload, displayText) => {
    setLoading(true);
    setPose((p) => ({ ...p, animation: "thinking" }));
    setMessages((prev) => [
      ...prev,
      { id: generateId(), role: "user", timestamp: new Date().toISOString(), rawContent: displayText, meta: null },
    ]);

    try {
      const res = await fetch(`${API_URL}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const raw = await res.json();
      const aiData = raw?.response ?? raw?.reply ?? raw?.data?.reply ?? raw?.message ?? raw;
      setDebugLastAiRaw(aiData);
      const rawContent = typeof aiData === "string" ? aiData : JSON.stringify(aiData ?? {});
      const meta = extractAssistantMeta(rawContent) || extractAssistantMeta(aiData);
      setMessages((prev) => [
        ...prev,
        { id: generateId(), role: "ai", timestamp: new Date().toISOString(), rawContent, meta },
      ]);
      const spoken = extractDisplayText("assistant", rawContent);
      const nextPose = {
        emotion: meta?.emotion || "neutral",
        animation: meta?.animation || "talk",
        voice_style: meta?.voice_style || "neutral",
      };
      setPose(nextPose);
      setCaption(spoken);
      if (spoken && !spoken.startsWith("[")) speak(spoken, nextPose.voice_style);
    } catch (err) {
      console.error("Send error:", err);
      setPose({ emotion: "calm", animation: "idle", voice_style: "soft" });
      setMessages((prev) => [
        ...prev,
        { id: generateId(), role: "ai", timestamp: new Date().toISOString(), rawContent: "Connection failed 😔", meta: null },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const sendText = () => {
    if (!input.trim()) return;
    sendPayload(
      {
        session_id: sessionId,
        user_id: userId,
        input: { type: "text", prompt: input },
        context: { device: "web", emotion: "neutral" },
      },
      input
    );
    setInput("");
  };

  const sendVision = () => {
    if (!image) return alert("Upload image first");
    sendPayload(
      {
        session_id: sessionId,
        user_id: userId,
        input: { type: "vision", prompt: "What do you see?", image_base64: image },
        context: { device: "web", camera: "upload" },
      },
      "[Uploaded Image]"
    );
    setImage(null);
  };

  const status = loading ? "Thinking…" : speaking ? "Talking…" : "Ready";

  return (
    <div className="app">
      <section className="stage">
        <div className="stage-canvas">
          <GoodBoiStage
            emotion={latestMeta.emotion}
            animation={latestMeta.animation}
            speaking={speaking}
            thinking={loading}
            audioLevel={audioLevel}
          />
        </div>
        <div className="stage-top">
          <div className="brand">
            <MaskMark size={26} />
            <div>
              <h1>GoodBoi</h1>
              <p>{status}</p>
            </div>
          </div>
          <div className="pills">
            {latestMeta.emotion ? <span className="pill">emotion: {latestMeta.emotion}</span> : null}
            {latestMeta.voice_style ? <span className="pill">voice: {latestMeta.voice_style}</span> : null}
            {latestMeta.animation ? <span className="pill">anim: {latestMeta.animation}</span> : null}
            <button
              type="button"
              onClick={() => {
                const next = !muted;
                setMuted(next);
                localStorage.setItem("goodboi_muted", next ? "1" : "0");
                if (next) stopVoice();
              }}
            >
              {muted ? "Unmute" : "Mute"}
            </button>
          </div>
        </div>
        {caption ? <div className="caption">{caption}</div> : null}
      </section>

      <aside className="chat">
        <header className="chat-head">
          <div>
            <strong>Chat</strong>
            <span>{loading ? "GoodBoi is thinking…" : "Memories + messages"}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              if (debugLastAiRaw) alert(JSON.stringify(debugLastAiRaw, null, 2));
              else alert("No AI payload yet.");
            }}
          >
            Debug
          </button>
        </header>

        <div className="thread" ref={scrollRef}>
          {messages.map((m) => {
            const isAi = m.role === "ai";
            const text = extractDisplayText(isAi ? "assistant" : "user", m.rawContent);
            return (
              <div key={m.id} className="row">
                <div className={`avatar ${isAi ? "bot" : ""}`}>{isAi ? <MaskMark size={18} /> : "You"}</div>
                <div className={`bubble ${isAi ? "ai" : "user"}`}>{text}</div>
              </div>
            );
          })}
          {loading ? (
            <div className="row">
              <div className="avatar bot">
                <MaskMark size={18} />
              </div>
              <div className="bubble ai">Thinking…</div>
            </div>
          ) : null}
        </div>

        {cameraEnabled ? (
          <div className="cam">
            <video ref={videoRef} autoPlay playsInline muted />
          </div>
        ) : null}

        <div className="composer">
          <textarea
            value={input}
            placeholder="Message GoodBoi…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
          />
          <div className="actions">
            <button className="primary" type="button" disabled={loading || !input.trim()} onClick={sendText}>
              Send
            </button>
            <label className="btn">
              <input type="file" accept="image/*" onChange={handleImageUpload} />
              {image ? "Image ready ✓" : "Upload image"}
            </label>
            <button type="button" disabled={!image || loading} onClick={sendVision}>
              Send image
            </button>
            <button type="button" onClick={loadMemory}>
              Reload
            </button>
            <button type="button" onClick={cameraEnabled ? stopCamera : startCamera}>
              {cameraEnabled ? "Cam on" : "Cam off"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
